import React from "react";
import formatValue from "../../utils/formatValue";
import {
  AlertsSection,
  AlertsSectionTitle,
  AlertsSectionSubtitle,
  AlertsList,
  AlertCard,
  AlertCardBody,
  AlertCardTitle,
  AlertCardMeta,
  AlertCardActions,
  AlertEditButton,
  AlertRemoveButton,
  AlertsEmpty,
  AlertsLoading,
} from "./styles";

export type PropertyAlertListItem = {
  id: number;
  city?: string | null;
  neighborhood?: string | null;
  max_rent?: number | string | null;
  min_rooms?: number | null;
  segment?: string | null;
  notify_email?: boolean;
  notify_whatsapp?: boolean;
  whatsapp_opt_in?: boolean;
  /** Opt-in já registrado no passado (backend aceita WA sem novo checkbox) */
  whatsapp_opt_in_at?: string | null;
  is_active?: boolean;
  property_type_id?: number | null;
  property_type?: { name?: string } | null;
};

function segmentLabel(segment: string | null | undefined): string {
  if (segment === "commercial") return "Comercial";
  if (segment === "residential") return "Residencial";
  return segment || "—";
}

function formatChannels(a: PropertyAlertListItem): string {
  const parts: string[] = [];
  if (a.notify_email) parts.push("E-mail");
  if (a.notify_whatsapp) parts.push("WhatsApp");
  return parts.length ? parts.join(" · ") : "—";
}

interface Props {
  loading: boolean;
  alerts: PropertyAlertListItem[];
  removingId: number | null;
  onRemove: (id: number) => void;
  onEdit: (alert: PropertyAlertListItem) => void;
}

export default function PropertyAlertsSection({
  loading,
  alerts,
  removingId,
  onRemove,
  onEdit,
}: Props) {
  return (
    <AlertsSection aria-labelledby="property-alerts-heading">
      <AlertsSectionTitle id="property-alerts-heading">
        Meus alertas de imóveis
      </AlertsSectionTitle>
      <AlertsSectionSubtitle>
        Você recebe aviso quando surgir imóvel compatível com estes critérios.
      </AlertsSectionSubtitle>

      {loading && <AlertsLoading>Carregando alertas…</AlertsLoading>}

      {!loading && alerts.length === 0 && (
        <AlertsEmpty>Nenhum alerta ativo. Crie um na busca de imóveis.</AlertsEmpty>
      )}

      {!loading && alerts.length > 0 && (
        <AlertsList>
          {alerts.map((a) => {
            const loc = [a.neighborhood, a.city].filter(Boolean).join(" · ");
            const rentNum =
              typeof a.max_rent === "number"
                ? a.max_rent
                : parseFloat(String(a.max_rent ?? ""));
            const maxRent = Number.isFinite(rentNum) ? formatValue(rentNum) : null;

            return (
              <AlertCard key={a.id}>
                <AlertCardBody>
                  <AlertCardTitle>
                    {loc || "Local a definir"}
                    {a.property_type?.name
                      ? ` · ${a.property_type.name}`
                      : null}
                  </AlertCardTitle>
                  <AlertCardMeta>
                    Até {maxRent ?? "—"} · Mín. {a.min_rooms ?? "—"} quartos ·{" "}
                    {segmentLabel(a.segment)}
                    <br />
                    Canais: {formatChannels(a)}
                  </AlertCardMeta>
                </AlertCardBody>
                <AlertCardActions>
                  <AlertEditButton
                    type="button"
                    onClick={() => onEdit(a)}
                  >
                    Editar
                  </AlertEditButton>
                  <AlertRemoveButton
                    type="button"
                    disabled={removingId === a.id}
                    onClick={() => onRemove(a.id)}
                  >
                    {removingId === a.id ? "Removendo…" : "Excluir alerta"}
                  </AlertRemoveButton>
                </AlertCardActions>
              </AlertCard>
            );
          })}
        </AlertsList>
      )}
    </AlertsSection>
  );
}
